import {
  BellOutlined,
  CameraOutlined,
  CheckCircleFilled,
  CloudServerOutlined,
  CopyOutlined,
  DeleteOutlined,
  EyeInvisibleOutlined,
  IdcardOutlined,
  MailOutlined,
  NotificationOutlined,
  PictureOutlined,
  RadarChartOutlined,
  SafetyCertificateFilled,
  SafetyCertificateOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Button, Card, ConfigProvider, Divider, Space, Tag, Typography } from 'antd';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import './PrivacyPolicy.css';

const { Paragraph, Text, Title, Link } = Typography;

const POLICY = {
  appName: '狐哩狐哩',
  operator: '狐哩狐哩开发者',
  email: 'xinyi.wzy@gmail.com',
  effectiveDate: '2026年8月11日',
  updatedDate: '2026年8月11日',
};

interface PolicySectionProps {
  index: number;
  title: string;
  children: ReactNode;
}

interface PermissionItemProps {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}

function PolicySection({ index, title, children }: PolicySectionProps) {
  return (
    <Card className='privacy-section' variant='borderless'>
      <div className='privacy-section-heading'>
        <span className='privacy-section-index'>{index}</span>
        <Title level={2}>{title}</Title>
      </div>
      <div className='privacy-section-content'>{children}</div>
    </Card>
  );
}

function Subsection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className='privacy-subsection'>
      <Title level={3}>{title}</Title>
      {children}
    </section>
  );
}

function PolicyList({ children }: { children: ReactNode }) {
  return <ul className='privacy-list'>{children}</ul>;
}

function PermissionItem({ icon, title, children }: PermissionItemProps) {
  return (
    <div className='privacy-permission-item'>
      <span className='privacy-permission-icon'>{icon}</span>
      <div>
        <Text strong>{title}</Text>
        <Paragraph>{children}</Paragraph>
      </div>
    </div>
  );
}

export default function PrivacyPolicy() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${POLICY.appName}隐私政策`;
    return () => {
      document.title = previousTitle;
    };
  }, []);

  const mailto = `mailto:${POLICY.email}?subject=${encodeURIComponent(`${POLICY.appName}隐私问题`)}`;

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#8565cc',
          colorInfo: '#8565cc',
          borderRadius: 16,
          fontFamily: 'Inter, "PingFang SC", "Microsoft YaHei", sans-serif',
        },
      }}>
      <main className='privacy-page'>
        <div className='privacy-background privacy-background-one' />
        <div className='privacy-background privacy-background-two' />

        <div className='privacy-container'>
          <header className='privacy-hero'>
            <div className='privacy-hero-orb privacy-hero-orb-large' />
            <div className='privacy-hero-orb privacy-hero-orb-small' />
            <span className='privacy-shield'><SafetyCertificateFilled /></span>
            <Text className='privacy-kicker'>HULI HULI · PRIVACY</Text>
            <Title>{POLICY.appName} 隐私政策</Title>
            <Paragraph>
              我们重视您的个人信息与选择权。本政策说明应用在不同功能中会处理哪些数据、为何处理，以及您如何管理这些数据。
            </Paragraph>
            <Space size={[8, 8]} wrap className='privacy-date-row'>
              <Tag>更新日期：{POLICY.updatedDate}</Tag>
              <Tag>生效日期：{POLICY.effectiveDate}</Tag>
            </Space>
          </header>

          <section className='privacy-promises' aria-label='隐私承诺'>
            <div><UserOutlined /><span>登录可选</span></div>
            <div><EyeInvisibleOutlined /><span>不跨应用跟踪</span></div>
            <div><NotificationOutlined /><span>不用于广告</span></div>
          </section>

          <div className='privacy-summary'>
            <CheckCircleFilled />
            <Paragraph>
              您无需登录即可使用应用的主要本地内容功能。只有在您主动注册、绑定平台账号、同步解锁内容或填写礼物收件信息时，我们才会处理对应信息。
            </Paragraph>
          </div>

          <PolicySection index={1} title='适用范围'>
            <Paragraph>
              本政策适用于 {POLICY.operator} 提供的“{POLICY.appName}”移动应用及其账号、内容和礼物相关服务。第三方网站、B站或抖音自身提供的服务受其各自隐私政策约束。
            </Paragraph>
          </PolicySection>

          <PolicySection index={2} title='我们处理的信息'>
            <Subsection title='2.1 账号与身份验证'>
              <PolicyList>
                <li>注册或使用账号时：用户名、邮箱地址、邮箱验证码及密码的安全散列值。我们不会在服务器保存您的明文密码。</li>
                <li>登录状态：账号标识、访问令牌、刷新令牌及其有效期。移动端登录凭据保存在系统安全存储中。</li>
                <li>为防止滥用和排查故障，服务器可能处理请求 IP、请求时间、应用或设备系统信息以及必要的错误日志。</li>
              </PolicyList>
            </Subsection>

            <Subsection title='2.2 平台账号绑定'>
              <PolicyList>
                <li>当您主动绑定 B站或抖音账号时，我们会保存平台类型、平台账号标识、绑定时间。</li>
                <li>为了向您展示绑定结果，我们会读取相应平台业务数据中的公开资料，例如昵称、头像；抖音账号还可能显示粉丝团灯牌等级及更新时间。</li>
                <li>这些数据用于确认账号归属、展示绑定状态和查询对应礼物，不用于建立广告画像。</li>
              </PolicyList>
            </Subsection>

            <Subsection title='2.3 礼物与收件信息'>
              <PolicyList>
                <li>礼物资格、礼物名称与类型、领取或发放状态、批次、截止时间、快递单号及线上礼物内容。</li>
                <li>当您主动填写实体礼物地址时：收件人姓名、联系电话和详细收件地址。</li>
                <li>收件信息仅用于确认领取资格、礼物发放、物流履约和必要的售后处理。</li>
              </PolicyList>
            </Subsection>

            <Subsection title='2.4 音声、主题与本地数据'>
              <PolicyList>
                <li>应用会在设备本地保存已解锁音声和主题的 URL、下载内容、播放所需缓存、主题偏好及应用设置。</li>
                <li>当您登录并使用云同步功能时，已解锁音声或主题的 URL 会与您的账号关联并上传至我们的服务器，用于多设备同步。</li>
                <li>账号资料的本地缓存用于离线展示；退出登录时会清除登录凭据和账号资料缓存。</li>
              </PolicyList>
            </Subsection>
          </PolicySection>

          <PolicySection index={3} title='设备权限与功能数据'>
            <div className='privacy-permissions'>
              <PermissionItem icon={<CameraOutlined />} title='相机、麦克风与 AR'>
                相机用于扫描二维码及您主动进入的 AR 功能。AR 组件可能请求麦克风权限以支持声音能力，但当前版本不会主动录音。扫码图像、AR 环境信息和音频不会由我们保存或上传；扫码得到的文本或 URL 可能发送至服务器解析跳转目标。
              </PermissionItem>
              <PermissionItem icon={<RadarChartOutlined />} title='NFC'>
                用于在您主动操作时读取或写入 NFC 标签。标签中的 URL 或文本可能发送至服务器验证并解析内容，不会在后台持续扫描。
              </PermissionItem>
              <PermissionItem icon={<PictureOutlined />} title='照片图库'>
                仅在您选择保存表情图片时请求写入权限。应用不会因此读取或上传您图库中的其他照片或视频。
              </PermissionItem>
              <PermissionItem icon={<BellOutlined />} title='通知'>
                用于显示音声播放控制和必要的播放状态。我们不会使用通知权限投放个性化广告。
              </PermissionItem>
              <PermissionItem icon={<CopyOutlined />} title='剪贴板'>
                只有在您点击复制时，应用才会把快递单号或线上礼物内容写入系统剪贴板；应用不会主动读取剪贴板内容。
              </PermissionItem>
            </div>
            <Paragraph className='privacy-permission-note'>
              您可以在系统设置中随时撤回相机、照片、NFC 或通知权限。撤回后，对应功能可能无法使用，但不影响其他无需该权限的功能。
            </Paragraph>
          </PolicySection>

          <PolicySection index={4} title='信息的使用目的'>
            <PolicyList>
              <li>创建和维护账号、完成邮箱验证、登录、令牌刷新与账号安全保护。</li>
              <li>完成 B站、抖音账号绑定，展示公开资料并查询对应礼物。</li>
              <li>同步音声和主题解锁记录，提供跨设备恢复能力。</li>
              <li>提供内容下载、播放、扫码、NFC、AR、表情保存和礼物履约功能。</li>
              <li>保障服务稳定与安全，定位网络错误、异常请求和服务故障。</li>
              <li>履行法律义务、处理争议并保护用户、开发者或公众的合法权益。</li>
            </PolicyList>
          </PolicySection>

          <PolicySection index={5} title='共享、委托处理与跟踪'>
            <div className='privacy-emphasis'>
              <SafetyCertificateOutlined />
              <Text strong>我们不会出售您的个人信息，不会使用您的信息进行跨应用或跨网站跟踪，也不会将其用于第三方广告。</Text>
            </div>
            <PolicyList>
              <li><strong>腾讯云邮件服务：</strong>接收您的邮箱地址和验证码邮件内容，用于发送注册、邮箱修改或密码重置验证码。</li>
              <li><strong>云服务器、数据库、对象存储与内容分发服务：</strong>用于托管账号数据、接口和应用内容，并完成安全运维。</li>
              <li><strong>B站与抖音相关数据：</strong>仅在您主动绑定、跳转或查询时，用于完成账号验证及展示相应平台公开资料和业务记录。</li>
              <li><strong>Apple 与操作系统服务：</strong>负责权限管理、安全存储、通知、系统诊断及 App Store 服务；Apple 对其自行处理的数据承担相应责任。</li>
            </PolicyList>
            <Paragraph>
              除上述服务所必需、取得您的另行同意，或法律法规及主管机关依法要求外，我们不会向其他第三方提供您的个人信息。
            </Paragraph>
          </PolicySection>

          <PolicySection index={6} title='保存、安全与跨境'>
            <PolicyList>
              <li>账号及绑定数据通常保存至您注销账号；礼物履约信息会在完成发放、售后和法定义务所需期限内保留。</li>
              <li>邮箱验证码通常在 10 分钟后失效；登录令牌具有有限有效期，并可在退出登录、密码重置或注销账号时失效。</li>
              <li>本地下载、缓存和偏好保存在您的设备上，您可以通过应用功能、系统设置或卸载应用进行清理。</li>
              <li>我们采取传输加密、密码散列、访问控制和令牌管理等合理措施，但任何网络或存储方式都无法保证绝对安全。</li>
              <li>服务及受托服务商可能在其运营地区处理数据；如发生受适用法律约束的跨境处理，我们会采取法律要求的保护措施。</li>
            </PolicyList>
          </PolicySection>

          <PolicySection index={7} title='您的选择与权利'>
            <PolicyList>
              <li>您可以不注册或不登录，继续使用无需账号的本地功能。</li>
              <li>登录后可在“账号管理”中查看账号资料、修改邮箱和密码、绑定或解绑平台账号。</li>
              <li>礼物页面允许您查看并在可修改期限内填写或修改收件信息。</li>
              <li>您可以随时退出登录；退出会撤销当前登录凭据并清除本地账号资料缓存，不会删除账号。</li>
              <li>您可以在“我的 → 账号管理 → 注销账号”中直接永久删除统一账号，无需联系客服。</li>
            </PolicyList>
            <div className='privacy-delete-notice'>
              <DeleteOutlined />
              <Paragraph>
                注销会删除统一账号、平台绑定、登录令牌及云端音声/主题解锁记录。本机已下载内容不会被远程删除。平台原始资料、直播互动及已进入履约流程的礼物记录属于独立业务记录，不会因解绑自动删除；如需处理其中的个人信息，请通过本页邮箱联系我们。
              </Paragraph>
            </div>
            <Paragraph>
              您还可以联系我们申请访问、更正或删除相关个人信息、撤回同意或询问数据处理情况。我们会在核验身份后按照适用法律处理。
            </Paragraph>
          </PolicySection>

          <PolicySection index={8} title='未成年人保护'>
            <Paragraph>
              本应用并非专门面向未满 14 周岁的儿童。未满 14 周岁的用户应在监护人阅读并同意本政策后使用需要提交个人信息的功能。如果监护人认为儿童在未经同意的情况下向我们提供了个人信息，请联系我们处理。
            </Paragraph>
          </PolicySection>

          <PolicySection index={9} title='政策更新'>
            <Paragraph>
              我们可能因功能、服务或法律要求变化而更新本政策，并在应用内展示最新版本和生效日期。对于处理目的、信息类型或您的权利产生重大影响的变更，我们会通过应用内提示等适当方式告知您。
            </Paragraph>
          </PolicySection>

          <Card className='privacy-contact' variant='borderless'>
            <div className='privacy-contact-icon'><MailOutlined /></div>
            <div className='privacy-contact-copy'>
              <Title level={2}>10. 联系我们</Title>
              <Paragraph>隐私问题、权利请求或平台业务数据处理</Paragraph>
              <Link href={mailto}>{POLICY.email}</Link>
            </div>
            <Button type='primary' size='large' icon={<MailOutlined />} href={mailto}>发送邮件</Button>
          </Card>

          <Divider />
          <footer className='privacy-footer'>
            <Space size={8}>
              <CloudServerOutlined />
              <Text type='secondary'>{POLICY.appName} © {new Date().getFullYear()}</Text>
            </Space>
            <Space size={8}>
              <IdcardOutlined />
              <Text type='secondary'>{POLICY.operator}</Text>
            </Space>
          </footer>
        </div>
      </main>
    </ConfigProvider>
  );
}
